import React, { createContext, useState, useContext, useCallback } from "react";
import {
  createNewItem,
  deleteItem,
  updateItem,
} from "../../../../lib/firebase/post/write";
import { useNavigate } from "react-router-dom";
import { getPost } from "../../../../lib/firebase/post/read";

const PostFormContext = createContext();

export function usePostForm() {
  return useContext(PostFormContext);
}

export default function PostFormContextProvider({ children }) {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isDone, setIsDone] = useState(false);
  const [image, setImage] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const navigate = useNavigate();

  const handleData = (key, value) => {
    setIsDone(false);
    setData((prevData) => ({
      ...prevData,
      [key]: value,
    }));
  };

  const handleCreate = useCallback(
    async (collectionName = "posts", formData = data) => {
      setError("");
      setIsLoading(true);
      setIsDone(false);
      try {
        console.log("Creating with data:", formData);
        await createNewItem({ data: formData, image, pdfFile, collectionName });
        setIsDone(true);
        setTimeout(() => {
          setIsDone(false);
          navigate("/admin/posts");
        }, 1000);
      } catch (error) {
        console.error("Create Error:", error);
        setError(error.message || "Failed to create item");
      } finally {
        setIsLoading(false);
      }
    },
    [data, image, pdfFile, navigate]
  );

  const handleUpdate = useCallback(
    async (collectionName = "posts", formData = data) => {
      setError("");
      setIsLoading(true);
      setIsDone(false);
      try {
        if (!formData?.id) {
          throw new Error("No ID provided for update");
        }
        console.log("Updating with data:", formData);
        await updateItem({ data: formData, image, pdfFile, collectionName });
        setIsDone(true);
        setTimeout(() => {
          setIsDone(false);
          navigate("/admin/posts");
        }, 1000);
      } catch (error) {
        console.error("Update Error:", error);
        setError(error.message || "Failed to update item");
      } finally {
        setIsLoading(false);
      }
    },
    [data, image, pdfFile, navigate]
  );

  const handleDelete = useCallback(
    async (id, collectionName = "posts") => {
      setError("");
      setIsLoading(true);
      setIsDone(false);
      try {
        if (!id) {
          throw new Error("No ID provided for deletion");
        }
        console.log("Deleting:", { collectionName, id });
        await deleteItem(id, collectionName);
        setIsDone(true);
        setTimeout(() => {
          setIsDone(false);
          navigate("/admin/posts");
        }, 1000);
      } catch (error) {
        console.error("Delete Error:", error);
        setError(error.message || "Failed to delete item");
      } finally {
        setIsLoading(false);
      }
    },
    [navigate]
  );

  const fetchData = useCallback(async (id, collectionName = "posts") => {
    setError("");
    setIsLoading(true);
    setIsDone(false);
    try {
      const res = await getPost(id, collectionName);
      if (res.exists()) {
        const fetchedData = res.data();
        console.log("Fetched Data:", fetchedData);
        setData(fetchedData);
      } else {
        throw new Error(`No ${collectionName.slice(0, -1)} found for id ${id}`);
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setError(error.message || "Failed to fetch item");
    } finally {
      setIsLoading(false);
    }
  }, []);

  return (
    <PostFormContext.Provider
      value={{
        data,
        handleData,
        isLoading,
        error,
        isDone,
        handleCreate,
        handleUpdate,
        handleDelete,
        fetchData,
        image,
        setImage,
        pdfFile,
        setPdfFile,
      }}
    >
      {children}
    </PostFormContext.Provider>
  );
}